import { Fragment, type ReactNode } from "react";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type ConsultationRecording,
  type RecordingStatus,
  type SortDirection,
  type SortKey,
} from "@/components/settings/consultation-recordings-table.types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

const HEADER_CLASS = "z-label uppercase tracking-wide text-muted-foreground";

const formatDateLabel = (date: Date | null) =>
  date ? format(date, "dd MMM yyyy") : "—";
const formatTimeLabel = (date: Date | null) =>
  date ? format(date, "hh:mm a") : "—";

const getStatusTone = (status: RecordingStatus) => {
  switch (status) {
    case "Ready":
      return "border-primary/30 bg-primary/10 text-primary";
    case "Processing":
      return "border-border/70 bg-muted text-foreground";
    case "Partial":
      return "border-border/70 bg-accent/40 text-accent-foreground";
    case "Failed":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    default:
      return "border-border/70 bg-muted text-muted-foreground";
  }
};

const highlightMatches = (text: string, query: string): ReactNode => {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return text;

  const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escapedQuery})`, "ig");

  return text.split(matcher).map((part, index) =>
    part.toLowerCase() === normalizedQuery.toLowerCase() ? (
      <mark
        key={`${text}-${part}-${index}`}
        className="rounded-sm bg-primary/15 px-0.5 text-foreground"
      >
        {part}
      </mark>
    ) : (
      <Fragment key={`${text}-${part}-${index}`}>{part}</Fragment>
    )
  );
};

interface ConsultationRecordingsTableProps {
  recordings: ConsultationRecording[];
  visibleColumns: Record<SortKey, boolean>;
  sortKey: SortKey;
  sortDirection: SortDirection;
  selectedRecordingId: string | null;
  searchQuery: string;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  onSort: (key: SortKey) => void;
  onSelectRecording: (recordingId: string) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export const ConsultationRecordingsTable = ({
  recordings,
  visibleColumns,
  sortKey,
  sortDirection,
  selectedRecordingId,
  searchQuery,
  currentPage,
  pageSize,
  totalPages,
  totalRecords,
  onSort,
  onSelectRecording,
  onPreviousPage,
  onNextPage,
}: ConsultationRecordingsTableProps) => {
  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/70" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5 text-foreground" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-foreground" />
    );
  };

  const renderSortableHeader = (key: SortKey, label: string) => (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 text-left hover:text-foreground transition-colors"
      onClick={() => onSort(key)}
    >
      {label}
      {renderSortIcon(key)}
    </button>
  );

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow className="hover:bg-card">
              {visibleColumns.date ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("date", "Date")}
                </TableHead>
              ) : null}
              {visibleColumns.startTime ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("startTime", "Start Time")}
                </TableHead>
              ) : null}
              {visibleColumns.patient ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("patient", "Patient")}
                </TableHead>
              ) : null}
              {visibleColumns.provider ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("provider", "Provider")}
                </TableHead>
              ) : null}
              {visibleColumns.segments ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("segments", "Segments")}
                </TableHead>
              ) : null}
              {visibleColumns.totalDuration ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("totalDuration", "Total Duration")}
                </TableHead>
              ) : null}
              {visibleColumns.status ? (
                <TableHead className={HEADER_CLASS}>
                  {renderSortableHeader("status", "Status")}
                </TableHead>
              ) : null}
              <TableHead className={cn(HEADER_CLASS, "w-[60px] text-right")}>
                <span className="sr-only">Open details</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recordings.map((recording) => {
              const isSelected = selectedRecordingId === recording.id;

              return (
                <TableRow
                  key={recording.id}
                  className={cn(
                    "cursor-pointer bg-card transition-colors hover:bg-muted/20",
                    isSelected && "bg-primary/5 hover:bg-primary/5"
                  )}
                  onClick={() => onSelectRecording(recording.id)}
                >
                  {visibleColumns.date ? (
                    <TableCell className="py-3 z-body tabular-nums text-foreground">
                      {formatDateLabel(recording.start)}
                    </TableCell>
                  ) : null}
                  {visibleColumns.startTime ? (
                    <TableCell className="py-3 z-body tabular-nums text-muted-foreground">
                      {formatTimeLabel(recording.start)}
                    </TableCell>
                  ) : null}
                  {visibleColumns.patient ? (
                    <TableCell className="py-3 z-body text-foreground">
                      {highlightMatches(recording.patient, searchQuery)}
                    </TableCell>
                  ) : null}
                  {visibleColumns.provider ? (
                    <TableCell className="py-3">
                      <div className="min-w-0 space-y-1">
                        <p className="truncate z-body text-foreground">
                          {highlightMatches(recording.provider, searchQuery)}
                        </p>
                        {recording.providerType ? (
                          <Badge
                            variant="outline"
                            className="rounded-full border-border/70 bg-background/80 px-2 py-0.5 z-caption font-normal text-muted-foreground shadow-none"
                          >
                            <span className="truncate">
                              {recording.providerType}
                            </span>
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                  ) : null}
                  {visibleColumns.segments ? (
                    <TableCell className="py-3">
                      {recording.segmentCount > 1 ? (
                        <Badge
                          variant="outline"
                          className="rounded-full border-border/70 bg-accent/40 px-2 py-0.5 z-caption font-medium text-accent-foreground shadow-none"
                        >
                          {recording.segmentCount}
                        </Badge>
                      ) : (
                        <span className="z-body tabular-nums text-muted-foreground">
                          {recording.segmentCount}
                        </span>
                      )}
                    </TableCell>
                  ) : null}
                  {visibleColumns.totalDuration ? (
                    <TableCell className="py-3 z-body tabular-nums text-muted-foreground">
                      {recording.totalDurationMinutes} min
                    </TableCell>
                  ) : null}
                  {visibleColumns.status ? (
                    <TableCell className="py-3">
                      <Badge
                        variant="outline"
                        className={cn(
                          "rounded-full px-2 py-0.5 z-caption font-medium shadow-none",
                          getStatusTone(recording.status)
                        )}
                      >
                        {recording.status}
                      </Badge>
                    </TableCell>
                  ) : null}
                  <TableCell className="py-3 text-right">
                    <ChevronRight
                      className={cn(
                        "ml-auto h-4 w-4 transition-colors",
                        isSelected
                          ? "text-foreground"
                          : "text-muted-foreground/60"
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex shrink-0 flex-col gap-3 border-t border-border/60 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="z-caption text-muted-foreground">
          Showing {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}{" "}
          recordings
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9 border-border/70 bg-background/80 px-3 shadow-none hover:bg-accent/20"
            disabled={currentPage === 1}
            onClick={onPreviousPage}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 border-border/70 bg-background/80 px-3 shadow-none hover:bg-accent/20"
            disabled={currentPage >= totalPages}
            onClick={onNextPage}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsultationRecordingsTable;
